import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Download, Filter, X, TrendingUp, BarChart3, Activity, Package, Truck, Factory, ChevronLeft } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, ComposedChart, Line
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const CustomTooltip = ({ active, payload, label, suffix }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: {suffix === '%' ? `${p.value}%` : p.value.toLocaleString()}
        </p>
      ))}
    </div>
  );
};

// Helper: get ISO week number
const getWeekNumber = (dateStr) => {
  const d = new Date(dateStr);
  const oneJan = new Date(d.getFullYear(), 0, 1);
  const days = Math.floor((d - oneJan) / 86400000);
  return Math.ceil((days + oneJan.getDay() + 1) / 7);
};

// Format "2026-02" → "Feb-26"
const formatMonthLabel = (monthKey) => {
  const [year, month] = monthKey.split('-');
  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${names[parseInt(month) - 1]}-${year.slice(2)}`;
};

// Build hierarchical: Month > Week > Date from daily data
const buildDrillData = (dailyData) => {
  const months = {};
  for (const row of dailyData) {
    const dt = row.date;
    const monthKey = dt.slice(0, 7);
    const weekNum = getWeekNumber(dt);
    const weekKey = `W${weekNum}`;

    if (!months[monthKey]) months[monthKey] = { key: monthKey, label: formatMonthLabel(monthKey), weeks: {}, scheduled_qty: 0, dispatched_qty: 0, inward_qty: 0, production_qty: 0, bop_fg_equivalent: 0, fg_dispatched_qty: 0, _fg_map: {} };
    months[monthKey].scheduled_qty += row.scheduled_qty || 0;
    months[monthKey].dispatched_qty += row.dispatched_qty || 0;
    months[monthKey].inward_qty += row.inward_qty || 0;
    months[monthKey].production_qty += row.production_qty || 0;
    months[monthKey].bop_fg_equivalent += row.bop_fg_equivalent || 0;
    months[monthKey].fg_dispatched_qty += row.fg_dispatched_qty || 0;

    // Aggregate fg_breakdown at month level
    if (row.fg_breakdown) {
      for (const fg of row.fg_breakdown) {
        if (!months[monthKey]._fg_map[fg.fg_id]) {
          months[monthKey]._fg_map[fg.fg_id] = { fg_id: fg.fg_id, fg_name: fg.fg_name, fg_code: fg.fg_code, bop_received_eq: 0, fg_dispatched: 0, _bop_map: {} };
        }
        months[monthKey]._fg_map[fg.fg_id].bop_received_eq += fg.bop_received_eq || 0;
        months[monthKey]._fg_map[fg.fg_id].fg_dispatched += fg.fg_dispatched || 0;
        // Aggregate bop_details
        if (fg.bop_details) {
          for (const bop of fg.bop_details) {
            const bKey = `${bop.bop_id}__${bop.vendor_name}`;
            if (!months[monthKey]._fg_map[fg.fg_id]._bop_map[bKey]) {
              months[monthKey]._fg_map[fg.fg_id]._bop_map[bKey] = { bop_id: bop.bop_id, bop_name: bop.bop_name, bop_code: bop.bop_code, vendor_name: bop.vendor_name, vendor_code: bop.vendor_code, received_qty: 0 };
            }
            months[monthKey]._fg_map[fg.fg_id]._bop_map[bKey].received_qty += bop.received_qty || 0;
          }
        }
      }
    }

    if (!months[monthKey].weeks[weekKey]) months[monthKey].weeks[weekKey] = { key: `${monthKey}-${weekKey}`, label: `${monthKey} ${weekKey}`, dates: [], scheduled_qty: 0, dispatched_qty: 0, inward_qty: 0, production_qty: 0, bop_fg_equivalent: 0, fg_dispatched_qty: 0, _fg_map: {} };
    months[monthKey].weeks[weekKey].scheduled_qty += row.scheduled_qty || 0;
    months[monthKey].weeks[weekKey].dispatched_qty += row.dispatched_qty || 0;
    months[monthKey].weeks[weekKey].inward_qty += row.inward_qty || 0;
    months[monthKey].weeks[weekKey].production_qty += row.production_qty || 0;
    months[monthKey].weeks[weekKey].bop_fg_equivalent += row.bop_fg_equivalent || 0;
    months[monthKey].weeks[weekKey].fg_dispatched_qty += row.fg_dispatched_qty || 0;

    // Aggregate fg_breakdown at week level
    if (row.fg_breakdown) {
      for (const fg of row.fg_breakdown) {
        if (!months[monthKey].weeks[weekKey]._fg_map[fg.fg_id]) {
          months[monthKey].weeks[weekKey]._fg_map[fg.fg_id] = { fg_id: fg.fg_id, fg_name: fg.fg_name, fg_code: fg.fg_code, bop_received_eq: 0, fg_dispatched: 0, _bop_map: {} };
        }
        months[monthKey].weeks[weekKey]._fg_map[fg.fg_id].bop_received_eq += fg.bop_received_eq || 0;
        months[monthKey].weeks[weekKey]._fg_map[fg.fg_id].fg_dispatched += fg.fg_dispatched || 0;
        // Aggregate bop_details
        if (fg.bop_details) {
          for (const bop of fg.bop_details) {
            const bKey = `${bop.bop_id}__${bop.vendor_name}`;
            if (!months[monthKey].weeks[weekKey]._fg_map[fg.fg_id]._bop_map[bKey]) {
              months[monthKey].weeks[weekKey]._fg_map[fg.fg_id]._bop_map[bKey] = { bop_id: bop.bop_id, bop_name: bop.bop_name, bop_code: bop.bop_code, vendor_name: bop.vendor_name, vendor_code: bop.vendor_code, received_qty: 0 };
            }
            months[monthKey].weeks[weekKey]._fg_map[fg.fg_id]._bop_map[bKey].received_qty += bop.received_qty || 0;
          }
        }
      }
    }

    months[monthKey].weeks[weekKey].dates.push({ key: dt, label: dt, ...row });
  }

  // Convert _fg_map to sorted fg_breakdown arrays
  const result = Object.values(months).map(m => {
    m.fg_breakdown = Object.values(m._fg_map).map(fg => {
      fg.bop_details = Object.values(fg._bop_map || {}).sort((a, b) => b.received_qty - a.received_qty);
      delete fg._bop_map;
      return fg;
    }).sort((a, b) => (b.bop_received_eq + b.fg_dispatched) - (a.bop_received_eq + a.fg_dispatched));
    delete m._fg_map;
    m.weeks = Object.values(m.weeks).map(w => {
      w.fg_breakdown = Object.values(w._fg_map).map(fg => {
        fg.bop_details = Object.values(fg._bop_map || {}).sort((a, b) => b.received_qty - a.received_qty);
        delete fg._bop_map;
        return fg;
      }).sort((a, b) => (b.bop_received_eq + b.fg_dispatched) - (a.bop_received_eq + a.fg_dispatched));
      delete w._fg_map;
      w.dates = w.dates.sort((a, b) => a.label.localeCompare(b.label));
      return w;
    }).sort((a, b) => a.key.localeCompare(b.key));
    return m;
  }).sort((a, b) => a.key.localeCompare(b.key));

  return result;
};

// Drillable Chart component: Month → Week → Date with click navigation
const DrillableChart = ({ hierarchy, bar1Key, bar1Name, bar1Color, bar2Key, bar2Name, bar2Color, showFillLine, chartId, customTooltip, onBarHover, onAllDataReady }) => {
  const [drillLevel, setDrillLevel] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);

  const chartData = useMemo(() => {
    if (drillLevel === 'month') {
      return hierarchy.map(m => ({
        label: m.label,
        [bar1Key]: m[bar1Key] || 0,
        [bar2Key]: m[bar2Key] || 0,
        fill_pct: m[bar1Key] > 0 ? Math.round(m[bar2Key] / m[bar1Key] * 100) : 0,
        _ref: m,
        fg_breakdown: m.fg_breakdown || []
      }));
    }
    if (drillLevel === 'week' && selectedMonth) {
      const month = hierarchy.find(m => m.key === selectedMonth);
      if (!month) return [];
      return month.weeks.map(w => ({
        label: w.label.split(' ').pop(), // Show just "W6"
        [bar1Key]: w[bar1Key] || 0,
        [bar2Key]: w[bar2Key] || 0,
        fill_pct: w[bar1Key] > 0 ? Math.round(w[bar2Key] / w[bar1Key] * 100) : 0,
        _ref: w,
        fg_breakdown: w.fg_breakdown || []
      }));
    }
    if (drillLevel === 'date' && selectedMonth && selectedWeek) {
      const month = hierarchy.find(m => m.key === selectedMonth);
      if (!month) return [];
      const week = month.weeks.find(w => w.key === selectedWeek);
      if (!week) return [];
      return week.dates.map(d => ({
        label: d.label.slice(5), // Show "03-11" instead of "2026-03-11"
        [bar1Key]: d[bar1Key] || 0,
        [bar2Key]: d[bar2Key] || 0,
        fill_pct: d[bar1Key] > 0 ? Math.round(d[bar2Key] / d[bar1Key] * 100) : 0,
        _ref: d,
        fg_breakdown: d.fg_breakdown || []
      }));
    }
    return [];
  }, [drillLevel, selectedMonth, selectedWeek, hierarchy, bar1Key, bar2Key]);

  const handleBarClick = (barData) => {
    if (!barData?.payload?._ref) return;
    // Fire hover callback with clicked bar's data
    if (onBarHover) onBarHover(barData.payload);
    if (drillLevel === 'month') {
      setSelectedMonth(barData.payload._ref.key);
      setDrillLevel('week');
    } else if (drillLevel === 'week') {
      setSelectedWeek(barData.payload._ref.key);
      setDrillLevel('date');
    }
  };

  // When chart data changes (drill level change), auto-select first bar and expose all data
  useEffect(() => {
    if (onBarHover && chartData.length > 0) {
      onBarHover(chartData[0]);
    }
    if (onAllDataReady) {
      onAllDataReady(chartData);
    }
  }, [drillLevel, selectedMonth, selectedWeek]); // deps intentionally limited to drill state

  const goBack = () => {
    if (drillLevel === 'date') { setSelectedWeek(null); setDrillLevel('week'); }
    else if (drillLevel === 'week') { setSelectedMonth(null); setDrillLevel('month'); }
  };

  const breadcrumb = useMemo(() => {
    const parts = [];
    parts.push({ label: 'Monthly', level: 'month', active: drillLevel === 'month' });
    if (selectedMonth) {
      const monthObj = hierarchy.find(m => m.key === selectedMonth);
      parts.push({ label: monthObj?.label || selectedMonth, level: 'week', active: drillLevel === 'week' });
    }
    if (selectedWeek) {
      const monthObj = hierarchy.find(m => m.key === selectedMonth);
      const weekObj = monthObj?.weeks.find(w => w.key === selectedWeek);
      parts.push({ label: weekObj?.label.split(' ').pop() || selectedWeek, level: 'date', active: drillLevel === 'date' });
    }
    return parts;
  }, [drillLevel, selectedMonth, selectedWeek, hierarchy]);

  const levelHint = drillLevel === 'month' ? 'Click a bar to drill into weeks' : drillLevel === 'week' ? 'Click a bar to drill into days' : 'Daily view (most granular)';

  return (
    <div>
      {/* Breadcrumb + Back */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1 text-sm">
          {drillLevel !== 'month' && (
            <Button variant="ghost" size="sm" onClick={goBack} className="h-7 px-2 mr-1" data-testid={`${chartId}-back-btn`}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
          )}
          {breadcrumb.map((b, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="text-slate-300 mx-1">/</span>}
              <span
                className={`${b.active ? 'font-semibold text-slate-800' : 'text-blue-600 cursor-pointer hover:underline'}`}
                onClick={() => {
                  if (b.level === 'month') { setDrillLevel('month'); setSelectedMonth(null); setSelectedWeek(null); }
                  else if (b.level === 'week') { setDrillLevel('week'); setSelectedWeek(null); }
                }}
                data-testid={`${chartId}-breadcrumb-${b.level}`}
              >
                {b.label}
              </span>
            </React.Fragment>
          ))}
        </div>
        <span className="text-xs text-slate-400">{levelHint}</span>
      </div>

      {/* Chart */}
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chartData} style={{ cursor: drillLevel !== 'date' ? 'pointer' : 'default' }}
            onMouseMove={(e) => { if (onBarHover && e?.activePayload?.[0]?.payload) onBarHover(e.activePayload[0].payload); }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" style={{ fontSize: '11px' }} />
            <YAxis yAxisId="qty" style={{ fontSize: '11px' }} tickFormatter={v => v.toLocaleString()} />
            {showFillLine && <YAxis yAxisId="pct" orientation="right" style={{ fontSize: '11px' }} tickFormatter={v => `${v}%`} domain={[0, 100]} />}
            <Tooltip content={customTooltip ? (props) => customTooltip({ ...props, drillLevel }) : ({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              return (
                <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
                  <p className="font-semibold text-slate-700 mb-1">{label}</p>
                  {payload.filter(p => p.dataKey !== 'fill_pct').map((p, i) => (
                    <p key={i} style={{ color: p.color }}>{p.name}: {p.value.toLocaleString()}</p>
                  ))}
                  {showFillLine && payload.find(p => p.dataKey === 'fill_pct') && (
                    <p className="text-red-600">Fill %: {payload.find(p => p.dataKey === 'fill_pct').value}%</p>
                  )}
                  {drillLevel !== 'date' && <p className="text-xs text-blue-500 mt-1 italic">Click to drill down</p>}
                </div>
              );
            }} />
            <Legend />
            <Bar yAxisId="qty" dataKey={bar1Key} fill={bar1Color} name={bar1Name} radius={[4, 4, 0, 0]} onClick={drillLevel !== 'date' ? handleBarClick : undefined} />
            <Bar yAxisId="qty" dataKey={bar2Key} fill={bar2Color} name={bar2Name} radius={[4, 4, 0, 0]} onClick={drillLevel !== 'date' ? handleBarClick : undefined} />
            {showFillLine && <Line yAxisId="pct" type="monotone" dataKey="fill_pct" stroke="#dc2626" strokeWidth={2} name="Fill %" dot={{ r: 4 }} />}
          </ComposedChart>
        </ResponsiveContainer>
      ) : <div className="h-[300px] flex items-center justify-center text-slate-400">No data at this level</div>}
    </div>
  );
};

// Simple tooltip for BOP vs FG chart (detail shown in panel below)
const BopFgTooltip = ({ active, payload, label, drillLevel }) => {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-bold text-slate-800 mb-1">{label}</p>
      <p className="text-cyan-600">BOP Received (FG Eq.): <span className="font-bold">{(data?.bop_fg_equivalent || 0).toLocaleString()}</span></p>
      <p className="text-pink-600">FG Dispatched: <span className="font-bold">{(data?.fg_dispatched_qty || 0).toLocaleString()}</span></p>
      {payload.find(p => p.dataKey === 'fill_pct') && (
        <p className="text-red-600">Fill %: <span className="font-bold">{payload.find(p => p.dataKey === 'fill_pct').value}%</span></p>
      )}
      {drillLevel !== 'date' && <p className="text-xs text-blue-500 mt-1 italic">Click to drill down</p>}
    </div>
  );
};

// FG Breakdown detail panel (shown below chart) with expandable BOP drill-down
const FgBreakdownPanel = ({ allBars, bar1Color, bar2Color }) => {
  const [expandedFg, setExpandedFg] = useState(null);

  if (!allBars || allBars.length === 0) return null;

  // Collect all unique FG items across all periods, including bop_details
  const fgMap = {};
  for (const bar of allBars) {
    const breakdown = bar.fg_breakdown || [];
    for (const fg of breakdown) {
      if (!fgMap[fg.fg_id]) {
        fgMap[fg.fg_id] = { fg_id: fg.fg_id, fg_name: fg.fg_name, fg_code: fg.fg_code, periods: {}, bop_map: {} };
      }
      fgMap[fg.fg_id].periods[bar.label] = { bop_received_eq: fg.bop_received_eq || 0, fg_dispatched: fg.fg_dispatched || 0 };
      if (fg.bop_details) {
        for (const bop of fg.bop_details) {
          const bKey = `${bop.bop_id}__${bop.vendor_name}`;
          if (!fgMap[fg.fg_id].bop_map[bKey]) {
            fgMap[fg.fg_id].bop_map[bKey] = { bop_id: bop.bop_id, bop_name: bop.bop_name, bop_code: bop.bop_code, vendor_name: bop.vendor_name, vendor_code: bop.vendor_code, periods: {} };
          }
          fgMap[fg.fg_id].bop_map[bKey].periods[bar.label] = (fgMap[fg.fg_id].bop_map[bKey].periods[bar.label] || 0) + (bop.received_qty || 0);
        }
      }
    }
  }

  const fgItems = Object.values(fgMap).sort((a, b) => {
    const aTotal = Object.values(a.periods).reduce((s, p) => s + p.bop_received_eq + p.fg_dispatched, 0);
    const bTotal = Object.values(b.periods).reduce((s, p) => s + p.bop_received_eq + p.fg_dispatched, 0);
    return bTotal - aTotal;
  });

  const periods = allBars.map(b => b.label);
  const colSpanPerPeriod = 3;
  const totalCols = 2 + (periods.length * colSpanPerPeriod) + (periods.length > 1 ? colSpanPerPeriod : 0);

  if (fgItems.length === 0) {
    return (
      <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-400">
        No FG breakdown data available
      </div>
    );
  }

  const periodTotals = {};
  for (const p of periods) {
    const bar = allBars.find(b => b.label === p);
    periodTotals[p] = { bop: bar?.bop_fg_equivalent || 0, disp: bar?.fg_dispatched_qty || 0 };
  }
  const grandBop = Object.values(periodTotals).reduce((s, t) => s + t.bop, 0);
  const grandDisp = Object.values(periodTotals).reduce((s, t) => s + t.disp, 0);

  // Excel download handler
  const handleDownload = () => {
    const XLSX = require('xlsx');
    const rows = [];
    // Header row 1: FG Item, Part No., period names (spanning 3 cols each), Total
    const h1 = ['FG Item', 'Part No.'];
    for (const p of periods) { h1.push(p, '', ''); }
    if (periods.length > 1) h1.push('Total', '', '');
    rows.push(h1);
    // Header row 2: sub-headers
    const h2 = ['', ''];
    for (const p of periods) { h2.push('BOP (FG Eq.)', 'Dispatched', 'Gap'); }
    if (periods.length > 1) h2.push('BOP (FG Eq.)', 'Dispatched', 'Gap');
    rows.push(h2);

    for (const fg of fgItems) {
      const row = [fg.fg_name, fg.fg_code];
      let tBop = 0, tDisp = 0;
      for (const p of periods) {
        const d = fg.periods[p] || { bop_received_eq: 0, fg_dispatched: 0 };
        row.push(d.bop_received_eq, d.fg_dispatched, d.bop_received_eq - d.fg_dispatched);
        tBop += d.bop_received_eq; tDisp += d.fg_dispatched;
      }
      if (periods.length > 1) row.push(tBop, tDisp, tBop - tDisp);
      rows.push(row);

      // Add BOP detail rows sorted by bop_code
      const bopList = Object.values(fg.bop_map).sort((a, b) => (a.bop_code || '').localeCompare(b.bop_code || ''));
      for (const bop of bopList) {
        const bRow = [`  ${bop.bop_name} (${bop.bop_code})`, bop.vendor_name];
        let bTotal = 0;
        for (const p of periods) {
          const qty = bop.periods[p] || 0;
          bRow.push(qty, '', '');
          bTotal += qty;
        }
        if (periods.length > 1) bRow.push(bTotal, '', '');
        rows.push(bRow);
      }
    }
    // Totals row
    const totalRow = ['Total', ''];
    for (const p of periods) {
      const t = periodTotals[p];
      totalRow.push(t.bop, t.disp, t.bop - t.disp);
    }
    if (periods.length > 1) totalRow.push(grandBop, grandDisp, grandBop - grandDisp);
    rows.push(totalRow);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    // Merge period header cells
    const merges = [];
    let col = 2;
    for (let i = 0; i < periods.length; i++) {
      merges.push({ s: { r: 0, c: col }, e: { r: 0, c: col + 2 } });
      col += 3;
    }
    if (periods.length > 1) merges.push({ s: { r: 0, c: col }, e: { r: 0, c: col + 2 } });
    ws['!merges'] = merges;
    // Set column widths
    ws['!cols'] = [{ wch: 24 }, { wch: 22 }, ...Array((periods.length + (periods.length > 1 ? 1 : 0)) * 3).fill({ wch: 14 })];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'FG Breakdown');
    XLSX.writeFile(wb, 'FG_Breakdown_Report.xlsx');
  };

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-white" data-testid="fg-breakdown-panel">
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200 rounded-t-lg flex-wrap gap-2">
        <span className="text-sm font-semibold text-slate-800">FG Breakdown — All Periods</span>
        <div className="flex items-center gap-4 text-xs">
          <span className="text-cyan-700 font-medium">Total BOP (FG Eq.): <span className="font-bold">{grandBop.toLocaleString()}</span></span>
          <span className="text-pink-700 font-medium">Total FG Dispatched: <span className="font-bold">{grandDisp.toLocaleString()}</span></span>
          <span className="text-red-600 font-medium">Fill: <span className="font-bold">{grandBop > 0 ? Math.round(grandDisp / grandBop * 100) : 0}%</span></span>
          <button
            onClick={handleDownload}
            className="ml-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors"
            data-testid="fg-download-excel-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Excel
          </button>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-white shadow-sm">
            <tr className="border-b border-slate-200">
              <th className="text-left py-2 px-4 text-xs text-slate-500 uppercase tracking-wider bg-white" rowSpan={2}>FG Item</th>
              <th className="text-left py-2 px-2 text-xs text-slate-500 uppercase tracking-wider bg-white" rowSpan={2}>Part No.</th>
              {periods.map(p => (
                <th key={p} className="text-center py-2 px-1 text-xs font-bold text-blue-700 uppercase tracking-wider border-l border-slate-100 bg-white" colSpan={3}>{p}</th>
              ))}
              {periods.length > 1 && (
                <th className="text-center py-2 px-1 text-xs font-bold text-slate-700 uppercase tracking-wider border-l border-slate-200 bg-white" colSpan={3}>Total</th>
              )}
            </tr>
            <tr className="text-[10px] text-slate-400 uppercase tracking-wider border-b border-slate-100">
              {periods.map(p => (
                <React.Fragment key={`sub-${p}`}>
                  <th className="py-1.5 px-2 text-right border-l border-slate-100 bg-white" style={{ color: bar1Color }}>BOP</th>
                  <th className="py-1.5 px-2 text-right bg-white" style={{ color: bar2Color }}>Disp</th>
                  <th className="py-1.5 px-2 text-right bg-white">Gap</th>
                </React.Fragment>
              ))}
              {periods.length > 1 && (
                <>
                  <th className="py-1.5 px-2 text-right border-l border-slate-200 bg-white" style={{ color: bar1Color }}>BOP</th>
                  <th className="py-1.5 px-2 text-right bg-white" style={{ color: bar2Color }}>Disp</th>
                  <th className="py-1.5 px-2 text-right bg-white">Gap</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {fgItems.map((fg, i) => {
              let totalBop = 0, totalDisp = 0;
              const isExpanded = expandedFg === fg.fg_id;
              // Sort BOP items by bop_code
              const bopItems = Object.values(fg.bop_map).sort((a, b) => (a.bop_code || '').localeCompare(b.bop_code || ''));
              const hasBop = bopItems.length > 0;

              return (
                <React.Fragment key={fg.fg_id}>
                  <tr
                    className={`border-b border-slate-50 hover:bg-slate-50 ${hasBop ? 'cursor-pointer' : ''} ${isExpanded ? 'bg-blue-50' : ''}`}
                    onClick={() => hasBop && setExpandedFg(isExpanded ? null : fg.fg_id)}
                    data-testid={`fg-detail-row-${i}`}
                  >
                    <td className="py-2.5 px-4 font-medium text-slate-800">
                      <span className="flex items-center gap-1.5">
                        {hasBop && <span className={`text-xs transition-transform ${isExpanded ? 'rotate-90' : ''}`}>&#9654;</span>}
                        {fg.fg_name}
                      </span>
                    </td>
                    <td className="py-2.5 px-2 text-slate-500 text-xs">{fg.fg_code}</td>
                    {periods.map(p => {
                      const d = fg.periods[p] || { bop_received_eq: 0, fg_dispatched: 0 };
                      const gap = d.bop_received_eq - d.fg_dispatched;
                      totalBop += d.bop_received_eq;
                      totalDisp += d.fg_dispatched;
                      return (
                        <React.Fragment key={`${fg.fg_id}-${p}`}>
                          <td className="py-2.5 px-2 text-right font-semibold text-cyan-700 border-l border-slate-50">{d.bop_received_eq.toLocaleString()}</td>
                          <td className="py-2.5 px-2 text-right font-semibold text-pink-700">{d.fg_dispatched.toLocaleString()}</td>
                          <td className={`py-2.5 px-2 text-right font-semibold ${gap >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {gap >= 0 ? `+${gap.toLocaleString()}` : gap.toLocaleString()}
                          </td>
                        </React.Fragment>
                      );
                    })}
                    {periods.length > 1 && (() => {
                      const totalGap = totalBop - totalDisp;
                      return (
                        <>
                          <td className="py-2.5 px-2 text-right font-bold text-cyan-800 border-l border-slate-200">{totalBop.toLocaleString()}</td>
                          <td className="py-2.5 px-2 text-right font-bold text-pink-800">{totalDisp.toLocaleString()}</td>
                          <td className={`py-2.5 px-2 text-right font-bold ${totalGap >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {totalGap >= 0 ? `+${totalGap.toLocaleString()}` : totalGap.toLocaleString()}
                          </td>
                        </>
                      );
                    })()}
                  </tr>

                  {/* Expanded BOP sub-rows */}
                  {isExpanded && (
                    <>
                      <tr className="bg-amber-50/50 border-b border-amber-100">
                        <td className="py-1.5 px-4 pl-10 text-[10px] font-semibold text-amber-800 uppercase tracking-wider">BOP Item</td>
                        <td className="py-1.5 px-2 text-[10px] font-semibold text-amber-800 uppercase tracking-wider">Vendor</td>
                        {periods.map(p => (
                          <React.Fragment key={`bop-hdr-${p}`}>
                            <td className="py-1.5 px-2 text-right text-[10px] font-semibold text-amber-700 border-l border-amber-100">Rcvd Qty</td>
                            <td className="py-1.5 px-2" colSpan={2}></td>
                          </React.Fragment>
                        ))}
                        {periods.length > 1 && (
                          <>
                            <td className="py-1.5 px-2 text-right text-[10px] font-semibold text-amber-800 border-l border-amber-200">Total Rcvd</td>
                            <td className="py-1.5 px-2" colSpan={2}></td>
                          </>
                        )}
                      </tr>
                      {bopItems.map((bop, j) => {
                        let bopTotal = 0;
                        return (
                          <tr key={`bop-${j}`} className="bg-amber-50/30 border-b border-amber-50 hover:bg-amber-50/60" data-testid={`bop-detail-row-${i}-${j}`}>
                            <td className="py-2 px-4 pl-10 text-xs text-slate-700">
                              <div className="font-medium">{bop.bop_name}</div>
                              <div className="text-[10px] text-slate-400">{bop.bop_code}</div>
                            </td>
                            <td className="py-2 px-2 text-xs text-slate-600">{bop.vendor_name}</td>
                            {periods.map(p => {
                              const qty = bop.periods[p] || 0;
                              bopTotal += qty;
                              return (
                                <React.Fragment key={`bop-${j}-${p}`}>
                                  <td className="py-2 px-2 text-right text-xs font-medium text-amber-800 border-l border-amber-50">{qty.toLocaleString()}</td>
                                  <td className="py-2 px-2" colSpan={2}></td>
                                </React.Fragment>
                              );
                            })}
                            {periods.length > 1 && (
                              <>
                                <td className="py-2 px-2 text-right text-xs font-bold text-amber-900 border-l border-amber-100">{bopTotal.toLocaleString()}</td>
                                <td className="py-2 px-2" colSpan={2}></td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                      {bopItems.length === 0 && (
                        <tr className="bg-amber-50/30">
                          <td colSpan={totalCols} className="py-2 px-4 pl-10 text-xs text-slate-400 italic">No BOP receipt data for this FG</td>
                        </tr>
                      )}
                    </>
                  )}
                </React.Fragment>
              );
            })}
            {/* Totals row */}
            <tr className="bg-slate-50 font-bold border-t border-slate-200">
              <td className="py-2.5 px-4 text-slate-800" colSpan={2}>Total</td>
              {periods.map(p => {
                const t = periodTotals[p];
                const gap = t.bop - t.disp;
                return (
                  <React.Fragment key={`total-${p}`}>
                    <td className="py-2.5 px-2 text-right text-cyan-800 border-l border-slate-100">{t.bop.toLocaleString()}</td>
                    <td className="py-2.5 px-2 text-right text-pink-800">{t.disp.toLocaleString()}</td>
                    <td className={`py-2.5 px-2 text-right ${gap >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {gap >= 0 ? `+${gap.toLocaleString()}` : gap.toLocaleString()}
                    </td>
                  </React.Fragment>
                );
              })}
              {periods.length > 1 && (() => {
                const totalGap = grandBop - grandDisp;
                return (
                  <>
                    <td className="py-2.5 px-2 text-right text-cyan-900 border-l border-slate-200">{grandBop.toLocaleString()}</td>
                    <td className="py-2.5 px-2 text-right text-pink-900">{grandDisp.toLocaleString()}</td>
                    <td className={`py-2.5 px-2 text-right ${totalGap >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                      {totalGap >= 0 ? `+${totalGap.toLocaleString()}` : totalGap.toLocaleString()}
                    </td>
                  </>
                );
              })()}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview');
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState('');

  const [dashboardData, setDashboardData] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [consumptionData, setConsumptionData] = useState(null);
  const [gapData, setGapData] = useState(null);
  const [drillConsVendor, setDrillConsVendor] = useState(null);
  const [drillPerfVendor, setDrillPerfVendor] = useState(null);
  const [drillProdItem, setDrillProdItem] = useState(null);
  const [expandedGap, setExpandedGap] = useState(null);
  const [selectedBopBar, setSelectedBopBar] = useState(null);
  const [allBopBars, setAllBopBars] = useState([]);

  useEffect(() => { fetchCustomers(); }, []);
  useEffect(() => { fetchAllData(); }, [customerFilter, startDate, endDate]);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/api/customers`);
      setCustomers(res.data);
    } catch (err) { console.error(err); }
  };

  const buildParams = () => {
    const params = new URLSearchParams();
    if (customerFilter && customerFilter !== 'all') params.append('customer_id', customerFilter);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return params.toString();
  };

  const fetchAllData = async () => {
    setLoading(true);
    setError('');
    try {
      const qs = buildParams();
      const [dashRes, dailyRes, consRes, gapRes] = await Promise.all([
        axios.get(`${API}/api/reports/analytics-dashboard?${qs}`),
        axios.get(`${API}/api/reports/schedule-dispatch-daily?${qs}`),
        axios.get(`${API}/api/reports/consumption-vs-supply?${qs}`),
        axios.get(`${API}/api/reports/supply-chain-gaps`)
      ]);
      setDashboardData(dashRes.data);
      setDailyData(dailyRes.data);
      setConsumptionData(consRes.data);
      setGapData(gapRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setCustomerFilter('all');
    setStartDate('');
    setEndDate('');
    setDrillConsVendor(null);
  };

  const hasFilters = customerFilter !== 'all' || startDate || endDate;

  // Build drillable hierarchies from daily data
  const schedDispHierarchy = useMemo(() => dailyData?.daily_data ? buildDrillData(dailyData.daily_data) : [], [dailyData]);
  const inwardDispHierarchy = useMemo(() => dailyData?.daily_data ? buildDrillData(dailyData.daily_data) : [], [dailyData]);

  const drillConsItems = useMemo(() => {
    if (!drillConsVendor || !consumptionData) return [];
    const v = consumptionData.vendors.find(x => x.vendor_id === drillConsVendor);
    return v ? v.items : [];
  }, [drillConsVendor, consumptionData]);

  const drillConsVendorName = useMemo(() => {
    if (!drillConsVendor || !consumptionData) return '';
    return consumptionData.vendors.find(x => x.vendor_id === drillConsVendor)?.vendor_name || '';
  }, [drillConsVendor, consumptionData]);

  if (loading) return <div className="flex items-center justify-center py-20 text-lg text-slate-500" data-testid="analytics-loading">Loading analytics...</div>;
  if (error) return <div className="p-6"><Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert></div>;

  const kpis = dashboardData?.kpis || {};

  return (
    <div className="space-y-5" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Analytics Dashboard</h2>
          <p className="text-slate-500 text-sm">Business intelligence — schedule, supply, dispatch & production</p>
        </div>
        {hasFilters && (
          <Button variant="outline" size="sm" onClick={clearFilters} data-testid="analytics-clear-filters">
            <X className="w-4 h-4 mr-1" /> Clear Filters
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-600" />
          <span className="text-sm font-semibold text-slate-700">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-medium mb-1 block text-slate-600">Customer</label>
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger className="h-9" data-testid="analytics-customer-filter"><SelectValue placeholder="All customers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All customers</SelectItem>
                {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block text-slate-600">Start Date</label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-9" data-testid="analytics-start-date" />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block text-slate-600">End Date</label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-9" data-testid="analytics-end-date" />
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: 'Total Schedule', value: kpis.total_schedule?.toLocaleString() || 0, icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Total Dispatch', value: kpis.total_dispatch?.toLocaleString() || 0, icon: Truck, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Total Inward', value: kpis.total_inward?.toLocaleString() || 0, icon: Package, color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'Total MRO Qty', value: kpis.total_ordered?.toLocaleString() || 0, icon: BarChart3, color: 'text-orange-600', bg: 'bg-orange-50' },
          { label: 'Schedule Fill %', value: `${kpis.schedule_fulfillment_pct || 0}%`, icon: Activity, color: kpis.schedule_fulfillment_pct >= 80 ? 'text-green-700' : 'text-red-600', bg: kpis.schedule_fulfillment_pct >= 80 ? 'bg-green-50' : 'bg-red-50' },
          { label: 'Procurement Fill %', value: `${kpis.procurement_fulfillment_pct || 0}%`, icon: Activity, color: kpis.procurement_fulfillment_pct >= 80 ? 'text-green-700' : 'text-red-600', bg: kpis.procurement_fulfillment_pct >= 80 ? 'bg-green-50' : 'bg-red-50' },
          { label: 'Production Yield', value: `${kpis.production_yield_pct || 0}%`, icon: Factory, color: kpis.production_yield_pct >= 90 ? 'text-green-700' : 'text-yellow-600', bg: kpis.production_yield_pct >= 90 ? 'bg-green-50' : 'bg-yellow-50' },
        ].map(({ label, value, icon: Icon, color, bg }, idx) => (
          <Card key={idx} className={`${bg} border-0`} data-testid={`analytics-kpi-${idx}`}>
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className={`text-xl font-bold ${color}`}>{value}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{label}</div>
                </div>
                <Icon className={`w-5 h-5 ${color} opacity-40`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Sub-Tabs */}
      <Tabs value={activeView} onValueChange={(v) => { setActiveView(v); setDrillConsVendor(null); setDrillPerfVendor(null); setDrillProdItem(null); }} className="w-full">
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="overview" data-testid="analytics-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="consumption-supply" data-testid="analytics-tab-consumption-supply">Consumption vs Supply</TabsTrigger>
          <TabsTrigger value="vendor-perf" data-testid="analytics-tab-vendor-perf">Vendor Performance</TabsTrigger>
          <TabsTrigger value="production" data-testid="analytics-tab-production">Production</TabsTrigger>
        </TabsList>

        {/* ===== OVERVIEW TAB ===== */}
        <TabsContent value="overview">
          <div className="space-y-6">
            {/* Top row: Schedule vs Dispatch + Production vs Dispatch side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Schedule vs Dispatch */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Schedule vs Dispatch</CardTitle>
                  <CardDescription>Click bars to drill: Month → Week → Date</CardDescription>
                </CardHeader>
                <CardContent>
                  {schedDispHierarchy.length > 0 ? (
                    <DrillableChart
                      hierarchy={schedDispHierarchy}
                      bar1Key="scheduled_qty" bar1Name="Scheduled Qty" bar1Color="#2563eb"
                      bar2Key="dispatched_qty" bar2Name="Dispatched Qty" bar2Color="#16a34a"
                      showFillLine={true}
                      chartId="sched-disp"
                    />
                  ) : <div className="h-[300px] flex items-center justify-center text-slate-400">No schedule/dispatch data</div>}
                </CardContent>
              </Card>

              {/* Production vs Dispatch */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Production vs Dispatch</CardTitle>
                  <CardDescription>Click bars to drill: Month → Week → Date</CardDescription>
                </CardHeader>
                <CardContent>
                  {schedDispHierarchy.length > 0 ? (
                    <DrillableChart
                      hierarchy={schedDispHierarchy}
                      bar1Key="production_qty" bar1Name="Production (Good) Qty" bar1Color="#8b5cf6"
                      bar2Key="dispatched_qty" bar2Name="Dispatched Qty" bar2Color="#f97316"
                      showFillLine={true}
                      chartId="prod-disp"
                    />
                  ) : <div className="h-[300px] flex items-center justify-center text-slate-400">No production/dispatch data</div>}
                </CardContent>
              </Card>
            </div>

            {/* Bottom row: Supply Chain Gap Analysis + BOP Received vs FG Dispatch side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Supply Chain Gap Analysis */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Supply Chain Gap Analysis</CardTitle>
                  <CardDescription>Click any metric to see details</CardDescription>
                </CardHeader>
                <CardContent>
                  {gapData ? (
                    <div className="space-y-2">
                      {[
                        { key: 'schedule_changes', label: 'Schedule Changes', desc: 'Times vendor schedule was revised', icon: '📋', color: 'blue', count: gapData.schedule_changes?.count || 0 },
                        { key: 'short_material', label: 'Short Material by Vendor', desc: 'Vendor sent less qty than MRO approved', icon: '📉', color: 'red', count: gapData.short_material?.count || 0 },
                        { key: 'mro_vs_ready', label: 'MRO Approved vs Ready Dispatch', desc: 'Gap between approved qty and ready qty', icon: '📦', color: 'orange', count: gapData.mro_vs_ready?.count || 0 },
                        { key: 'ready_vs_transit', label: 'Ready Dispatch vs In Transit', desc: 'Pickup/dispatch delays', icon: '🚛', color: 'yellow', count: gapData.ready_vs_transit?.count || 0 },
                        { key: 'transit_vs_grn', label: 'In Transit vs GRN Received', desc: 'Delivery/receiving gaps', icon: '🏭', color: 'purple', count: gapData.transit_vs_grn?.count || 0 },
                      ].map(({ key, label, desc, color, count }) => {
                        const isExpanded = expandedGap === key;
                        const details = gapData[key]?.details || [];
                        const colorMap = { blue: 'bg-blue-50 border-blue-200 text-blue-700', red: 'bg-red-50 border-red-200 text-red-700', orange: 'bg-orange-50 border-orange-200 text-orange-700', yellow: 'bg-amber-50 border-amber-200 text-amber-700', purple: 'bg-purple-50 border-purple-200 text-purple-700' };
                        const countColor = count > 0 ? { blue: 'text-blue-600', red: 'text-red-600', orange: 'text-orange-600', yellow: 'text-amber-600', purple: 'text-purple-600' }[color] : 'text-green-600';
                        return (
                          <div key={key}>
                            <div
                              className={`rounded-lg border p-3 cursor-pointer transition-all hover:shadow-sm ${isExpanded ? colorMap[color] : 'bg-white border-slate-200'}`}
                              onClick={() => setExpandedGap(isExpanded ? null : key)}
                              data-testid={`gap-card-${key}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                                    {details.length > 0 && <span className={`text-xs transition-transform ${isExpanded ? 'rotate-90' : ''}`}>&#9654;</span>}
                                    {label}
                                  </div>
                                  <div className="text-[11px] text-slate-500 mt-0.5">{desc}</div>
                                </div>
                                <div className={`text-2xl font-bold ${countColor}`}>{count}</div>
                              </div>
                            </div>
                            {isExpanded && details.length > 0 && (
                              <div className="mt-1 ml-4 mr-1 border border-slate-100 rounded-lg overflow-hidden">
                                <table className="w-full text-xs">
                                  <thead className="bg-slate-50">
                                    <tr>
                                      {key === 'schedule_changes' ? (
                                        <><th className="py-1.5 px-2 text-left text-slate-600">Item</th><th className="py-1.5 px-2 text-right text-slate-600">Revisions</th><th className="py-1.5 px-2 text-right text-slate-600">Current Qty</th></>
                                      ) : key === 'short_material' ? (
                                        <><th className="py-1.5 px-2 text-left text-slate-600">Vendor / Item</th><th className="py-1.5 px-2 text-right text-slate-600">MRO Qty</th><th className="py-1.5 px-2 text-right text-slate-600">Shipped</th><th className="py-1.5 px-2 text-right text-red-600">Short</th></>
                                      ) : key === 'mro_vs_ready' ? (
                                        <><th className="py-1.5 px-2 text-left text-slate-600">Vendor / Item</th><th className="py-1.5 px-2 text-right text-slate-600">MRO</th><th className="py-1.5 px-2 text-right text-slate-600">Ready</th><th className="py-1.5 px-2 text-right text-red-600">Gap</th></>
                                      ) : key === 'ready_vs_transit' ? (
                                        <><th className="py-1.5 px-2 text-left text-slate-600">Vendor / Item</th><th className="py-1.5 px-2 text-right text-slate-600">Ready</th><th className="py-1.5 px-2 text-right text-slate-600">Transit</th><th className="py-1.5 px-2 text-right text-red-600">Gap</th></>
                                      ) : (
                                        <><th className="py-1.5 px-2 text-left text-slate-600">Vendor / Item</th><th className="py-1.5 px-2 text-right text-slate-600">Transit</th><th className="py-1.5 px-2 text-right text-slate-600">GRN</th><th className="py-1.5 px-2 text-right text-red-600">Gap</th></>
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {details.slice(0, 10).map((d, i) => (
                                      <tr key={i} className="border-t border-slate-50 hover:bg-slate-50">
                                        {key === 'schedule_changes' ? (
                                          <><td className="py-1.5 px-2"><div className="font-medium">{d.item_name}</div><div className="text-[10px] text-slate-400">{d.item_code}</div></td><td className="py-1.5 px-2 text-right font-bold text-blue-600">{d.revision_count}</td><td className="py-1.5 px-2 text-right">{d.current_qty?.toLocaleString()}</td></>
                                        ) : key === 'short_material' ? (
                                          <><td className="py-1.5 px-2"><div className="font-medium">{d.vendor}</div><div className="text-[10px] text-slate-400">{d.item} ({d.shipment})</div></td><td className="py-1.5 px-2 text-right">{d.mro_qty?.toLocaleString()}</td><td className="py-1.5 px-2 text-right">{d.shipped_qty?.toLocaleString()}</td><td className="py-1.5 px-2 text-right font-bold text-red-600">{d.short_qty?.toLocaleString()}</td></>
                                        ) : key === 'mro_vs_ready' ? (
                                          <><td className="py-1.5 px-2"><div className="font-medium">{d.vendor}</div><div className="text-[10px] text-slate-400">{d.item} ({d.shipment})</div></td><td className="py-1.5 px-2 text-right">{d.mro_qty?.toLocaleString()}</td><td className="py-1.5 px-2 text-right">{d.ready_qty?.toLocaleString()}</td><td className="py-1.5 px-2 text-right font-bold text-red-600">{d.gap?.toLocaleString()}</td></>
                                        ) : key === 'ready_vs_transit' ? (
                                          <><td className="py-1.5 px-2"><div className="font-medium">{d.vendor}</div><div className="text-[10px] text-slate-400">{d.item} ({d.shipment})</div></td><td className="py-1.5 px-2 text-right">{d.ready_qty?.toLocaleString()}</td><td className="py-1.5 px-2 text-right">{d.transit_qty?.toLocaleString()}</td><td className="py-1.5 px-2 text-right font-bold text-red-600">{d.gap?.toLocaleString()}</td></>
                                        ) : (
                                          <><td className="py-1.5 px-2"><div className="font-medium">{d.vendor}</div><div className="text-[10px] text-slate-400">{d.item} ({d.shipment})</div></td><td className="py-1.5 px-2 text-right">{d.transit_qty?.toLocaleString()}</td><td className="py-1.5 px-2 text-right">{d.grn_qty?.toLocaleString()}</td><td className="py-1.5 px-2 text-right font-bold text-red-600">{d.gap?.toLocaleString()}</td></>
                                        )}
                                      </tr>
                                    ))}
                                    {details.length > 10 && (
                                      <tr><td colSpan={4} className="py-1 px-2 text-center text-[10px] text-slate-400">+{details.length - 10} more</td></tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <div className="text-[10px] text-slate-400 text-right mt-2">Based on {gapData.total_shipments} shipments</div>
                    </div>
                  ) : <div className="h-[300px] flex items-center justify-center text-slate-400">No gap data</div>}
                </CardContent>
              </Card>

              {/* BOP Received (FG Eq.) vs FG Dispatch */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">BOP Received (FG Eq.) vs FG Dispatch</CardTitle>
                  <CardDescription>BOP received converted to FG equivalent via BOM — hover/click bars for FG detail</CardDescription>
                </CardHeader>
                <CardContent>
                  {schedDispHierarchy.length > 0 ? (
                    <>
                      <DrillableChart
                        hierarchy={schedDispHierarchy}
                        bar1Key="bop_fg_equivalent" bar1Name="BOP Received (FG Eq.)" bar1Color="#06b6d4"
                        bar2Key="fg_dispatched_qty" bar2Name="FG Dispatched Qty" bar2Color="#ec4899"
                        showFillLine={true}
                        chartId="bop-fg"
                        customTooltip={BopFgTooltip}
                        onBarHover={setSelectedBopBar}
                        onAllDataReady={setAllBopBars}
                      />
                      <FgBreakdownPanel allBars={allBopBars} bar1Color="#06b6d4" bar2Color="#ec4899" />
                    </>
                  ) : <div className="h-[300px] flex items-center justify-center text-slate-400">No BOP/FG data</div>}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ===== CONSUMPTION VS SUPPLY TAB ===== */}
        <TabsContent value="consumption-supply">
          <div className="space-y-6">
            {consumptionData && (
              <div className={`rounded-lg p-4 flex items-center justify-between ${consumptionData.overall_utilization_pct <= 100 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`} data-testid="cons-utilization-banner">
                <div className="flex items-center gap-3">
                  <div className={`text-3xl font-bold ${consumptionData.overall_utilization_pct <= 100 ? 'text-green-700' : 'text-yellow-700'}`}>
                    {consumptionData.overall_utilization_pct}%
                  </div>
                  <div>
                    <div className="font-medium text-slate-800">Overall Material Utilization</div>
                    <div className="text-xs text-slate-500">
                      Supply: {consumptionData.total_supply?.toLocaleString()} | Consumption: {consumptionData.total_consumption?.toLocaleString()} | Balance: {(consumptionData.total_supply - consumptionData.total_consumption).toLocaleString()}
                    </div>
                  </div>
                </div>
                <Badge className={consumptionData.overall_utilization_pct <= 100 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                  {consumptionData.overall_utilization_pct <= 100 ? 'Balanced' : 'Over-consumed'}
                </Badge>
              </div>
            )}

            {/* Consumption vs Supply bar chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Consumption vs Supply by Vendor</CardTitle>
                <CardDescription>Click a vendor bar to see item-level breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                {consumptionData?.vendors?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={Math.max(250, consumptionData.vendors.length * 55)}>
                    <BarChart data={consumptionData.vendors} layout="vertical" onClick={(e) => {
                      if (e?.activePayload?.[0]?.payload) {
                        const v = e.activePayload[0].payload;
                        setDrillConsVendor(drillConsVendor === v.vendor_id ? null : v.vendor_id);
                      }
                    }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" style={{ fontSize: '11px' }} />
                      <YAxis dataKey="vendor_name" type="category" width={150} style={{ fontSize: '11px', cursor: 'pointer' }} />
                      <Tooltip content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const data = payload[0]?.payload;
                        return (
                          <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
                            <p className="font-semibold text-slate-700 mb-1">{label}</p>
                            {payload.map((p, i) => <p key={i} style={{ color: p.color }}>{p.name}: {p.value.toLocaleString()}</p>)}
                            {data && <p className="text-slate-500 mt-1 pt-1 border-t border-slate-100">Utilization: {data.utilization_pct}% | Balance: {data.balance.toLocaleString()}</p>}
                          </div>
                        );
                      }} />
                      <Legend />
                      <Bar dataKey="supply_qty" fill="#8b5cf6" name="Supply (GRN)" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="consumption_qty" fill="#f97316" name="Consumption" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="h-[250px] flex items-center justify-center text-slate-400">No consumption data</div>}
              </CardContent>
            </Card>

            {/* Consumption vs Supply Table */}
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base">Vendor Consumption Summary</CardTitle>
                <button
                  onClick={() => {
                    const XLSX = require('xlsx');
                    const rows = [['Vendor', 'Supply Qty', 'Consumption Qty', 'Balance', 'Utilization %']];
                    for (const v of (consumptionData?.vendors || [])) {
                      rows.push([v.vendor_name, v.supply_qty, v.consumption_qty, v.balance, v.utilization_pct]);
                      if (v.items?.length) {
                        rows.push(['', 'Item', 'Code', 'Supply', 'Consumption', 'Balance']);
                        for (const it of v.items) {
                          rows.push(['', it.item_name, it.item_code, it.supply_qty, it.consumption_qty, it.supply_qty - it.consumption_qty]);
                        }
                      }
                    }
                    const ws = XLSX.utils.aoa_to_sheet(rows);
                    ws['!cols'] = [{ wch: 24 }, { wch: 22 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 12 }];
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, 'Consumption vs Supply');
                    XLSX.writeFile(wb, 'Consumption_vs_Supply.xlsx');
                  }}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors"
                  data-testid="consumption-download-btn"
                >
                  <Download className="w-3.5 h-3.5" /> Excel
                </button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Vendor</TableHead>
                        <TableHead className="text-right">Supply Qty</TableHead>
                        <TableHead className="text-right">Consumption Qty</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                        <TableHead className="text-right">Utilization</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {consumptionData?.vendors?.map((v, i) => (
                        <TableRow key={i} className="cursor-pointer hover:bg-slate-50" onClick={() => setDrillConsVendor(drillConsVendor === v.vendor_id ? null : v.vendor_id)} data-testid={`cons-vendor-row-${i}`}>
                          <TableCell className="font-medium">{v.vendor_name}</TableCell>
                          <TableCell className="text-right">{v.supply_qty.toLocaleString()}</TableCell>
                          <TableCell className="text-right">{v.consumption_qty.toLocaleString()}</TableCell>
                          <TableCell className={`text-right font-medium ${v.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>{v.balance.toLocaleString()}</TableCell>
                          <TableCell className="text-right">
                            <Badge className={v.utilization_pct <= 100 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{v.utilization_pct}%</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Drill-down items for consumption */}
            {drillConsVendor && drillConsItems.length > 0 && (
              <Card className="border-purple-200">
                <CardHeader className="pb-2 bg-purple-50 rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base text-purple-800">Item Breakdown: {drillConsVendorName}</CardTitle>
                      <CardDescription>{drillConsItems.length} items</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setDrillConsVendor(null)} data-testid="cons-clear-drill"><X className="w-4 h-4 mr-1" /> Close</Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead className="text-right">Supply</TableHead>
                        <TableHead className="text-right">Consumption</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {drillConsItems.map((item, i) => {
                        const bal = item.supply_qty - item.consumption_qty;
                        return (
                          <TableRow key={i} data-testid={`cons-item-row-${i}`}>
                            <TableCell className="font-medium text-sm">{item.item_name}</TableCell>
                            <TableCell className="text-sm text-slate-500">{item.item_code}</TableCell>
                            <TableCell className="text-right text-sm">{item.supply_qty.toLocaleString()}</TableCell>
                            <TableCell className="text-right text-sm">{item.consumption_qty.toLocaleString()}</TableCell>
                            <TableCell className={`text-right text-sm font-medium ${bal >= 0 ? 'text-green-600' : 'text-red-600'}`}>{bal.toLocaleString()}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ===== VENDOR PERFORMANCE TAB ===== */}
        <TabsContent value="vendor-perf">
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Vendor Fulfillment & Quality Rate</CardTitle>
                <CardDescription>Click a vendor bar to see item-level breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                {dashboardData?.vendor_performance?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={Math.max(220, dashboardData.vendor_performance.length * 55)}>
                    <BarChart data={dashboardData.vendor_performance} layout="vertical" onClick={(e) => {
                      if (e?.activePayload?.[0]?.payload) {
                        const v = e.activePayload[0].payload;
                        setDrillPerfVendor(drillPerfVendor === v.vendor_id ? null : v.vendor_id);
                      }
                    }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} style={{ fontSize: '11px' }} tickFormatter={v => `${v}%`} />
                      <YAxis dataKey="vendor_name" type="category" width={150} style={{ fontSize: '11px', cursor: 'pointer' }} />
                      <Tooltip content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0]?.payload;
                        return (
                          <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
                            <p className="font-semibold text-slate-700 mb-1">{label}</p>
                            {payload.map((p, i) => <p key={i} style={{ color: p.color }}>{p.name}: {p.value}%</p>)}
                            {d && <p className="text-slate-500 mt-1 pt-1 border-t border-slate-100">Ordered: {d.total_ordered?.toLocaleString()} | Received: {d.total_received?.toLocaleString()} | Items: {d.items?.length || 0}</p>}
                          </div>
                        );
                      }} />
                      <Legend />
                      <Bar dataKey="fulfillment_pct" fill="#2563eb" name="Fulfillment %" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="quality_pct" fill="#16a34a" name="Quality %" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="h-[220px] flex items-center justify-center text-slate-400">No vendor data</div>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base">Vendor Scorecard</CardTitle>
                <button
                  onClick={() => {
                    const XLSX = require('xlsx');
                    const rows = [['Vendor', 'MRO Count', 'Ordered', 'Received', 'Accepted', 'Rejected', 'Fulfillment %', 'Quality %']];
                    const vp = dashboardData?.vendor_performance || [];
                    for (const v of vp) {
                      rows.push([v.vendor_name, v.po_count, v.total_ordered, v.total_received, v.total_good, v.total_rejected, v.fulfillment_pct, v.quality_pct]);
                      if (v.items?.length) {
                        rows.push(['', '', 'Item', 'Code', 'Ordered', 'Received', 'Good', 'Rejected', 'Fulfillment %', 'Quality %']);
                        for (const it of v.items) {
                          rows.push(['', '', it.item_name, it.item_code, it.ordered, it.received, it.good, it.rejected, it.fulfillment_pct, it.quality_pct]);
                        }
                      }
                    }
                    const ws = XLSX.utils.aoa_to_sheet(rows);
                    ws['!cols'] = [{ wch: 24 }, { wch: 10 }, { wch: 22 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, 'Vendor Scorecard');
                    XLSX.writeFile(wb, 'Vendor_Scorecard.xlsx');
                  }}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors"
                  data-testid="vendor-perf-download-btn"
                >
                  <Download className="w-3.5 h-3.5" /> Excel
                </button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Vendor</TableHead>
                        <TableHead className="text-right">MRO Count</TableHead>
                        <TableHead className="text-right">Ordered</TableHead>
                        <TableHead className="text-right">Received</TableHead>
                        <TableHead className="text-right">Accepted</TableHead>
                        <TableHead className="text-right">Rejected</TableHead>
                        <TableHead className="text-right">Fulfillment</TableHead>
                        <TableHead className="text-right">Quality</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboardData?.vendor_performance?.map((v, i) => (
                        <React.Fragment key={i}>
                          <TableRow
                            className={`cursor-pointer hover:bg-slate-50 ${drillPerfVendor === v.vendor_id ? 'bg-blue-50' : ''}`}
                            onClick={() => setDrillPerfVendor(drillPerfVendor === v.vendor_id ? null : v.vendor_id)}
                            data-testid={`vendor-scorecard-row-${i}`}
                          >
                            <TableCell className="font-medium">
                              <span className="flex items-center gap-1.5">
                                {v.items?.length > 0 && <span className={`text-xs transition-transform ${drillPerfVendor === v.vendor_id ? 'rotate-90' : ''}`}>&#9654;</span>}
                                {v.vendor_name}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">{v.po_count}</TableCell>
                            <TableCell className="text-right">{v.total_ordered.toLocaleString()}</TableCell>
                            <TableCell className="text-right">{v.total_received.toLocaleString()}</TableCell>
                            <TableCell className="text-right text-green-600">{v.total_good.toLocaleString()}</TableCell>
                            <TableCell className="text-right text-red-600">{v.total_rejected.toLocaleString()}</TableCell>
                            <TableCell className="text-right"><Badge className={v.fulfillment_pct >= 80 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{v.fulfillment_pct}%</Badge></TableCell>
                            <TableCell className="text-right"><Badge className={v.quality_pct >= 95 ? 'bg-green-100 text-green-800' : v.quality_pct >= 80 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>{v.quality_pct}%</Badge></TableCell>
                          </TableRow>
                          {/* Item drill-down */}
                          {drillPerfVendor === v.vendor_id && v.items?.length > 0 && (
                            <>
                              <TableRow className="bg-indigo-50/50">
                                <TableCell className="py-1.5 pl-10 text-[10px] font-semibold text-indigo-800 uppercase tracking-wider" colSpan={2}>Item</TableCell>
                                <TableCell className="py-1.5 text-right text-[10px] font-semibold text-indigo-800 uppercase">Ordered</TableCell>
                                <TableCell className="py-1.5 text-right text-[10px] font-semibold text-indigo-800 uppercase">Received</TableCell>
                                <TableCell className="py-1.5 text-right text-[10px] font-semibold text-indigo-800 uppercase">Good</TableCell>
                                <TableCell className="py-1.5 text-right text-[10px] font-semibold text-indigo-800 uppercase">Rejected</TableCell>
                                <TableCell className="py-1.5 text-right text-[10px] font-semibold text-indigo-800 uppercase">Fulfill %</TableCell>
                                <TableCell className="py-1.5 text-right text-[10px] font-semibold text-indigo-800 uppercase">Quality %</TableCell>
                              </TableRow>
                              {v.items.map((it, j) => (
                                <TableRow key={`item-${j}`} className="bg-indigo-50/30 hover:bg-indigo-50/50" data-testid={`vendor-item-row-${i}-${j}`}>
                                  <TableCell className="pl-10 text-xs">
                                    <div className="font-medium text-slate-700">{it.item_name}</div>
                                    <div className="text-[10px] text-slate-400">{it.item_code}</div>
                                  </TableCell>
                                  <TableCell />
                                  <TableCell className="text-right text-xs">{it.ordered.toLocaleString()}</TableCell>
                                  <TableCell className="text-right text-xs">{it.received.toLocaleString()}</TableCell>
                                  <TableCell className="text-right text-xs text-green-600">{it.good.toLocaleString()}</TableCell>
                                  <TableCell className="text-right text-xs text-red-600">{it.rejected.toLocaleString()}</TableCell>
                                  <TableCell className="text-right"><Badge className={`text-[10px] ${it.fulfillment_pct >= 80 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{it.fulfillment_pct}%</Badge></TableCell>
                                  <TableCell className="text-right"><Badge className={`text-[10px] ${it.quality_pct >= 95 ? 'bg-green-100 text-green-800' : it.quality_pct >= 80 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>{it.quality_pct}%</Badge></TableCell>
                                </TableRow>
                              ))}
                            </>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== PRODUCTION TAB ===== */}
        <TabsContent value="production">
          <div className="space-y-6">
            {dashboardData?.production_summary && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {[
                    { label: 'Total Planned', value: dashboardData.production_summary.total_planned.toLocaleString(), color: 'text-blue-600', bg: 'bg-blue-50' },
                    { label: 'Good Qty', value: dashboardData.production_summary.total_good.toLocaleString(), color: 'text-green-600', bg: 'bg-green-50' },
                    { label: 'Scrap Qty', value: dashboardData.production_summary.total_scrap.toLocaleString(), color: 'text-red-600', bg: 'bg-red-50' },
                    { label: 'Completed', value: dashboardData.production_summary.completed, color: 'text-green-700', bg: 'bg-green-50' },
                    { label: 'Running', value: dashboardData.production_summary.running, color: 'text-yellow-600', bg: 'bg-yellow-50' },
                    { label: 'Planned', value: dashboardData.production_summary.planned, color: 'text-slate-600', bg: 'bg-slate-50' },
                  ].map(({ label, value, color, bg }, i) => (
                    <Card key={i} className={`${bg} border-0`} data-testid={`prod-kpi-${i}`}>
                      <CardContent className="pt-4 pb-3 px-4">
                        <div className={`text-xl font-bold ${color}`}>{value}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Production Order Status</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Completed', value: dashboardData.production_summary.completed, color: '#16a34a' },
                              { name: 'Running', value: dashboardData.production_summary.running, color: '#eab308' },
                              { name: 'Planned', value: dashboardData.production_summary.planned, color: '#94a3b8' },
                            ].filter(d => d.value > 0)}
                            cx="50%" cy="50%" outerRadius={90} innerRadius={45} dataKey="value"
                            label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                            labelLine={false}
                          >
                            {[{ color: '#16a34a' }, { color: '#eab308' }, { color: '#94a3b8' }]
                              .filter((_, i) => [dashboardData.production_summary.completed, dashboardData.production_summary.running, dashboardData.production_summary.planned][i] > 0)
                              .map((entry, i) => <Cell key={i} fill={entry.color} />)}
                          </Pie>
                          <Tooltip /><Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Production by FG Item</CardTitle>
                      <CardDescription>Click a bar to see work order details</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {dashboardData.production_summary.items?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={Math.max(220, dashboardData.production_summary.items.length * 55)}>
                          <BarChart data={dashboardData.production_summary.items} layout="vertical" onClick={(e) => {
                            if (e?.activePayload?.[0]?.payload) {
                              const it = e.activePayload[0].payload;
                              setDrillProdItem(drillProdItem === it.item_id ? null : it.item_id);
                            }
                          }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" style={{ fontSize: '11px' }} />
                            <YAxis dataKey="item_name" type="category" width={150} style={{ fontSize: '10px', cursor: 'pointer' }} />
                            <Tooltip content={({ active, payload, label }) => {
                              if (!active || !payload?.length) return null;
                              const d = payload[0]?.payload;
                              return (
                                <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
                                  <p className="font-semibold text-slate-700 mb-1">{label}</p>
                                  {payload.map((p, i) => <p key={i} style={{ color: p.color }}>{p.name}: {p.value.toLocaleString()}</p>)}
                                  {d && <p className="text-slate-500 mt-1 pt-1 border-t border-slate-100">Yield: {d.yield_pct}% | Orders: {d.total_orders}</p>}
                                </div>
                              );
                            }} />
                            <Legend />
                            <Bar dataKey="good_qty" fill="#16a34a" name="Good Qty" radius={[0, 4, 4, 0]} />
                            <Bar dataKey="scrap_qty" fill="#ef4444" name="Scrap Qty" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : <div className="h-[220px] flex items-center justify-center text-slate-400">No production data</div>}
                    </CardContent>
                  </Card>
                </div>

                <div className={`rounded-lg p-4 flex items-center gap-3 ${kpis.production_yield_pct >= 90 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <div className={`text-3xl font-bold ${kpis.production_yield_pct >= 90 ? 'text-green-700' : 'text-yellow-700'}`}>{kpis.production_yield_pct}%</div>
                  <div>
                    <div className="font-medium text-slate-800">Production Yield</div>
                    <div className="text-xs text-slate-500">Good: {dashboardData.production_summary.total_good.toLocaleString()} / Planned: {dashboardData.production_summary.total_planned.toLocaleString()} | Scrap: {dashboardData.production_summary.total_scrap.toLocaleString()}</div>
                  </div>
                </div>

                {/* Production FG Items Table with drill-down */}
                <Card>
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-base">Production Summary by FG Item</CardTitle>
                    <button
                      onClick={() => {
                        const XLSX = require('xlsx');
                        const rows = [['FG Item', 'Part No.', 'Plan Qty', 'Good Qty', 'Scrap Qty', 'Yield %', 'Completed', 'Running', 'Planned', 'Total Orders']];
                        const items = dashboardData.production_summary.items || [];
                        for (const it of items) {
                          rows.push([it.item_name, it.item_code, it.plan_qty, it.good_qty, it.scrap_qty, it.yield_pct, it.completed, it.running, it.planned, it.total_orders]);
                          if (it.orders?.length) {
                            rows.push(['', 'WO Number', 'Date', 'Plan Qty', 'Good Qty', 'Scrap Qty', 'Status']);
                            for (const wo of it.orders) {
                              rows.push(['', wo.wo_number, wo.prod_date, wo.plan_qty, wo.good_qty, wo.scrap_qty, wo.status]);
                            }
                          }
                        }
                        rows.push([]);
                        rows.push(['Total', '', dashboardData.production_summary.total_planned, dashboardData.production_summary.total_good, dashboardData.production_summary.total_scrap,
                          kpis.production_yield_pct, dashboardData.production_summary.completed, dashboardData.production_summary.running, dashboardData.production_summary.planned]);
                        const ws = XLSX.utils.aoa_to_sheet(rows);
                        ws['!cols'] = [{ wch: 24 }, { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }];
                        const wb = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(wb, ws, 'Production Summary');
                        XLSX.writeFile(wb, 'Production_Summary.xlsx');
                      }}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors"
                      data-testid="production-download-btn"
                    >
                      <Download className="w-3.5 h-3.5" /> Excel
                    </button>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>FG Item</TableHead>
                            <TableHead>Part No.</TableHead>
                            <TableHead className="text-right">Plan Qty</TableHead>
                            <TableHead className="text-right">Good Qty</TableHead>
                            <TableHead className="text-right">Scrap</TableHead>
                            <TableHead className="text-right">Yield</TableHead>
                            <TableHead className="text-right">Orders</TableHead>
                            <TableHead className="text-right">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dashboardData.production_summary.items?.map((it, i) => (
                            <React.Fragment key={i}>
                              <TableRow
                                className={`cursor-pointer hover:bg-slate-50 ${drillProdItem === it.item_id ? 'bg-violet-50' : ''}`}
                                onClick={() => setDrillProdItem(drillProdItem === it.item_id ? null : it.item_id)}
                                data-testid={`prod-item-row-${i}`}
                              >
                                <TableCell className="font-medium">
                                  <span className="flex items-center gap-1.5">
                                    {it.orders?.length > 0 && <span className={`text-xs transition-transform ${drillProdItem === it.item_id ? 'rotate-90' : ''}`}>&#9654;</span>}
                                    {it.item_name}
                                  </span>
                                </TableCell>
                                <TableCell className="text-xs text-slate-500">{it.item_code}</TableCell>
                                <TableCell className="text-right">{it.plan_qty.toLocaleString()}</TableCell>
                                <TableCell className="text-right text-green-600">{it.good_qty.toLocaleString()}</TableCell>
                                <TableCell className="text-right text-red-600">{it.scrap_qty.toLocaleString()}</TableCell>
                                <TableCell className="text-right"><Badge className={it.yield_pct >= 90 ? 'bg-green-100 text-green-800' : it.yield_pct >= 70 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>{it.yield_pct}%</Badge></TableCell>
                                <TableCell className="text-right">{it.total_orders}</TableCell>
                                <TableCell className="text-right text-xs">
                                  <span className="text-green-600">{it.completed}C</span> / <span className="text-yellow-600">{it.running}R</span> / <span className="text-slate-500">{it.planned}P</span>
                                </TableCell>
                              </TableRow>

                              {/* Work order drill-down */}
                              {drillProdItem === it.item_id && it.orders?.length > 0 && (
                                <>
                                  <TableRow className="bg-violet-50/50">
                                    <TableCell className="py-1.5 pl-10 text-[10px] font-semibold text-violet-800 uppercase tracking-wider" colSpan={2}>Work Order</TableCell>
                                    <TableCell className="py-1.5 text-right text-[10px] font-semibold text-violet-800 uppercase">Date</TableCell>
                                    <TableCell className="py-1.5 text-right text-[10px] font-semibold text-violet-800 uppercase">Plan</TableCell>
                                    <TableCell className="py-1.5 text-right text-[10px] font-semibold text-violet-800 uppercase">Good</TableCell>
                                    <TableCell className="py-1.5 text-right text-[10px] font-semibold text-violet-800 uppercase">Scrap</TableCell>
                                    <TableCell className="py-1.5 text-right text-[10px] font-semibold text-violet-800 uppercase">Yield</TableCell>
                                    <TableCell className="py-1.5 text-right text-[10px] font-semibold text-violet-800 uppercase">Status</TableCell>
                                  </TableRow>
                                  {it.orders.map((wo, j) => {
                                    const woYield = wo.plan_qty > 0 ? Math.round(wo.good_qty / wo.plan_qty * 100) : 0;
                                    return (
                                      <TableRow key={`wo-${j}`} className="bg-violet-50/30 hover:bg-violet-50/50" data-testid={`prod-wo-row-${i}-${j}`}>
                                        <TableCell className="pl-10 text-xs font-medium text-slate-700" colSpan={2}>{wo.wo_number}</TableCell>
                                        <TableCell className="text-right text-xs">{wo.prod_date}</TableCell>
                                        <TableCell className="text-right text-xs">{wo.plan_qty.toLocaleString()}</TableCell>
                                        <TableCell className="text-right text-xs text-green-600">{wo.good_qty.toLocaleString()}</TableCell>
                                        <TableCell className="text-right text-xs text-red-600">{wo.scrap_qty.toLocaleString()}</TableCell>
                                        <TableCell className="text-right">
                                          <Badge className={`text-[10px] ${woYield >= 90 ? 'bg-green-100 text-green-800' : woYield >= 70 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>{woYield}%</Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                          <Badge className={`text-[10px] ${wo.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : wo.status === 'RUNNING' ? 'bg-yellow-100 text-yellow-800' : 'bg-slate-100 text-slate-600'}`}>{wo.status}</Badge>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </>
                              )}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsDashboard;
